import { IconLoader2 } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { useEffect, useMemo } from 'react';
import classNames from 'classnames';
import NSDropdown from 'bricks/NSDropdown/NSDropdown';
import { IOptionAnyValue } from 'bricks/types';
import { LAND_DEVELOPMENT_COMING_SOON_TOOLTIP } from 'constants/proForma';
import { isLandDevelopmentModel } from 'helpers/proForma';
import { useGetProFormasByDeal } from 'queries/ProForma/useGetProFormasByDeal';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { Link } from 'react-router-dom';

export interface IProFormaSelectorProps {
    dealId: number;
    proFormaId?: number;
    onChangeProFormaSelection: (newSelection: IOptionAnyValue) => void;
    dropdownClassName?: string;
    containerClassName?: string;
    showDefaultOption?: boolean;
    isLoading?: boolean;
    disableLandDevelopmentOptions?: boolean;
}

const ProFormaSelector = ({
    dealId, proFormaId, onChangeProFormaSelection, dropdownClassName, containerClassName,
    showDefaultOption = false, isLoading = false, disableLandDevelopmentOptions = false,
}: IProFormaSelectorProps) => {
    const { data: proFormas } = useGetProFormasByDeal(
        { dealId, proFormaStatusIds: [PRO_FORMA_STATUS.SUBSTANTIALLY_COMPLETE] },
    );

    const proFormasOptions: IOptionAnyValue[] = useMemo(() => {
        if (proFormas && proFormas.length) {
            return proFormas.map(proForma => {
                const isDisabled = disableLandDevelopmentOptions && isLandDevelopmentModel(proForma.proFormaModelId);
                return {
                    label: proForma.name,
                    value: proForma.id,
                    disabled: isDisabled,
                    tooltip: isDisabled ? LAND_DEVELOPMENT_COMING_SOON_TOOLTIP : undefined,
                };
            });
        }

        return [
            {
                label: 'Create a pro forma first',
                component: (
                    <span>
                        <Link to={`/deal/${dealId}/pro_forma`} state={{ openCreateProForma: true }}>
                            Create a pro forma first
                        </Link>{' '}
                    </span>
                ),
                value: 'no-pro-forma',
            },
        ];
    }, [proFormas, dealId, disableLandDevelopmentOptions]);

    const selectedProFormaOption = proFormasOptions?.find(proForma => proForma.value === proFormaId);

    useEffect(() => {
        if (showDefaultOption && proFormas?.length && !selectedProFormaOption) {
            const activeProForma = proFormas.find(proForma => proForma.isActive);
            if (activeProForma) {
                onChangeProFormaSelection({
                    label: activeProForma.name,
                    value: activeProForma.id,
                });
            } else {
                onChangeProFormaSelection(proFormasOptions[0]);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDefaultOption, proFormasOptions, selectedProFormaOption]);

    return (
        <div className="d-flex align-items-center">
            <NSDropdown
                containerClassName={classNames('mt-1 pr-1', containerClassName)}
                options={proFormasOptions}
                selectedOption={selectedProFormaOption}
                handleSelectOption={onChangeProFormaSelection}
                placeholder="Select pro forma"
                isFullWidth
                toggleClassName={dropdownClassName}
            />
            {isLoading && <NSIcon icon={IconLoader2} />}
        </div>
    );
};

export default ProFormaSelector;
